/**
 * CriticalLoggingService.js
 * Centralized logging service for critical recording states and debugging
 */

import { getAccessTokenSilentlyOutsideComponent } from "./workspace.service";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api";

export class CriticalLoggingService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.isEnabled = true; // Can be controlled via environment variable
  }

  /**
   * Generate a unique session ID for this page load
   */
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Log a critical event to the backend
   */
  async logCriticalEvent(logData) {
    if (!this.isEnabled) return;

    try {
      const token = await getAccessTokenSilentlyOutsideComponent();
      if (!token) {
        console.warn("[CriticalLogging] No auth token available, skipping log");
        return;
      }

      const payload = {
        logLevel: logData.level || "info",
        category: logData.category,
        message: logData.message,
        data: logData.data || {},
        audioFileId: logData.audioFileId,
        sessionId: this.sessionId,
        userAgent: navigator.userAgent,
        url: globalThis.location.href,
      };

      console.log("[CriticalLogging] Sending log:", payload);

      const response = await fetch(`${API_BASE_URL}/logs/critical`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log("[CriticalLogging] Log sent successfully");
    } catch (error) {
      console.error("[CriticalLogging] Failed to send log:", error);
      // Don't throw to avoid disrupting user experience
    }
  }

  // Convenience methods for different log categories

  /**
   * Log recording state changes with complete UI state snapshot
   */
  logRecordingState(action, data = {}) {
    return this.logCriticalEvent({
      level: "info",
      category: "RECORDING_STATE",
      message: `Recording ${action}`,
      data: {
        action,
        timestamp: new Date().toISOString(),
        ...data,
      },
      audioFileId: data.audioFileId,
    });
  }

  /**
   * Log complete UI state snapshot at critical moments
   */
  logUIStateSnapshot(context, uiState = {}) {
    return this.logCriticalEvent({
      level: "info",
      category: "RECORDING_STATE",
      message: `UI state snapshot: ${context}`,
      data: {
        context,
        timestamp: new Date().toISOString(),
        timeSincePageLoad: Date.now() - performance.timing.navigationStart,
        url: globalThis.location.href,
        ...uiState,
      },
      audioFileId: uiState.audioFileId,
    });
  }

  /**
   * Log orphaned session detection and handling
   */
  logOrphanedSession(sessionData = {}) {
    return this.logCriticalEvent({
      level: "warn",
      category: "ORPHANED_SESSION",
      message: "Orphaned session detected and processed",
      data: {
        timestamp: new Date().toISOString(),
        timeSincePageLoad: Date.now() - performance.timing.navigationStart,
        ...sessionData,
      },
      audioFileId: sessionData.audioFileId,
    });
  }

  /**
   * Log chunk upload status
   */
  logChunkUpload(status, chunkInfo = {}) {
    return this.logCriticalEvent({
      level: status === "success" ? "info" : "error",
      category: "CHUNK_UPLOAD",
      message: `Chunk upload ${status}`,
      data: {
        status,
        timestamp: new Date().toISOString(),
        ...chunkInfo,
      },
      audioFileId: chunkInfo.audioFileId,
    });
  }

  /**
   * Log MediaRecorder state changes
   */
  logMediaRecorderState(state, info = {}) {
    return this.logCriticalEvent({
      level: info.isError ? "error" : "info",
      category: "MEDIA_RECORDER",
      message: `MediaRecorder state: ${state}`,
      data: {
        state,
        timestamp: new Date().toISOString(),
        ...info,
      },
      audioFileId: info.audioFileId,
    });
  }

  /**
   * Log critical errors
   */
  logError(error, context = {}) {
    return this.logCriticalEvent({
      level: "error",
      category: "ERROR_TRACKING",
      message: error.message || "Unknown error occurred",
      data: {
        error: error.toString(),
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
      },
      audioFileId: context.audioFileId,
    });
  }

  /**
   * Log emergency upload attempts
   */
  logEmergencyUpload(uploadData = {}) {
    return this.logCriticalEvent({
      level: "warn",
      category: "CHUNK_UPLOAD",
      message: "Emergency upload triggered",
      data: {
        trigger: "page_refresh",
        timestamp: new Date().toISOString(),
        ...uploadData,
      },
      audioFileId: uploadData.audioFileId,
    });
  }

  /**
   * Log recording session recovery
   */
  logSessionRecovery(recoveryData = {}) {
    return this.logCriticalEvent({
      level: "info",
      category: "ORPHANED_SESSION",
      message: "Session recovery completed",
      data: {
        timestamp: new Date().toISOString(),
        ...recoveryData,
      },
      audioFileId: recoveryData.audioFileId,
    });
  }

  /**
   * Disable logging (for testing or performance)
   */
  disable() {
    this.isEnabled = false;
  }

  /**
   * Enable logging
   */
  enable() {
    this.isEnabled = true;
  }
}

// Create singleton instance
const criticalLoggingService = new CriticalLoggingService();
export default criticalLoggingService;
